import './accounts.css';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fmtDayMonth, initials, monthShort } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL } from '../../types';
import { focusPill } from '../belege/useBelegeOverview';
import type { ChatStand, Stand, TeamStatusClient, TeamStatusData, TeamStatusRow } from './types';

// Chef-Übersicht auf der Startseite des Admins: je Mitarbeiter Stand, Mandanten, fehlende Belege, Monate zu buchen
// und ungelesene Chats. Ein Klick auf den Mitarbeiter klappt seine Mandanten auf.

const STAND: Record<Stand, { label: string; tone: string }> = {
  hinterher: { label: 'hinterher', tone: 'bad' },
  zu_tun: { label: 'zu tun', tone: 'warn' },
  ok: { label: 'im Plan', tone: 'ok' },
};

const monate = (n: number) => (n === 1 ? '1 Monat' : `${n} Monate`);

export function TeamStatus() {
  const res = useApi<TeamStatusData>('/api/kanzlei/team/status', { live: ['belege', 'chat', 'clients', 'team', 'fristen'] });
  const [open, setOpen] = useState<Set<string>>(new Set());
  if (!res.data) return null;
  const rows = res.data.rows;
  const staffRows = rows.filter((r) => r.staff && r.clients.length);
  const late = staffRows.filter((r) => r.stand === 'hinterher').length;
  const todo = staffRows.filter((r) => r.stand === 'zu_tun').length;
  const summary = [late && `${late} hinterher`, todo && `${todo} zu tun`].filter(Boolean).join(' · ') || 'alle im Plan';

  const toggle = (key: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  return (
    <div className="card chef acc">
      <div className="ch">
        <h3>Team im Blick</h3>
        {staffRows.length > 0 && <span>{summary}</span>}
      </div>
      {!rows.some((r) => r.staff) ? (
        <p className="note empty-note">
          Noch keine Mitarbeiter. Unter <Link to="/kanzlei/team">Team &amp; Zuweisung</Link> laden Sie Ihr Team ein.
        </p>
      ) : (
        <>
          <div className="tw">
            <table className="tchef">
              <thead>
                <tr>
                  <th>Mitarbeiter</th>
                  <th>Stand</th>
                  <th>Mandanten</th>
                  <th>Belege offen</th>
                  <th>Zu buchen</th>
                  <th>Chat</th>
                </tr>
              </thead>
              {rows.map((r) => {
                const key = r.staff?.id ?? 'offen';
                return <StaffRows key={key} r={r} open={open.has(key)} onToggle={() => toggle(key)} />;
              })}
            </table>
          </div>
          <p className="note legend">
            Hinterher: Ein Monat ist nach der UStVA-Frist noch nicht gebucht oder eine Nachricht ist seit mehr als 2 Werktagen
            ungelesen. Zu buchen: Monate mit Belegen, die noch nicht als gebucht markiert sind.
          </p>
        </>
      )}
    </div>
  );
}

function StaffRows({ r, open, onToggle }: { r: TeamStatusRow; open: boolean; onToggle(): void }) {
  const name = r.staff?.name ?? 'Nicht zugewiesen';
  const sub = r.staff ? AREA_LABEL[r.staff.area ?? 'fibu'] : 'übernimmt die Kanzleileitung';
  const id = `chef-${r.staff?.id ?? 'offen'}`;
  const has = r.clients.length > 0;
  return (
    <tbody className={open ? 'open' : undefined}>
      <tr className={`srow${has ? ' click' : ''}${r.staff ? '' : ' none'}`} onClick={has ? onToggle : undefined}>
        <td>
          <div className="who">
            <span className="av" aria-hidden="true">
              {r.staff ? initials(r.staff.name) : '–'}
            </span>
            <span className="nm">
              {has ? (
                <button
                  type="button"
                  className="tg"
                  aria-expanded={open}
                  aria-controls={id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                  }}
                >
                  {name}
                  <span aria-hidden="true">{open ? ' ▾' : ' ▸'}</span>
                </button>
              ) : (
                <b>{name}</b>
              )}
              <span className="sub">{sub}</span>
            </span>
          </div>
        </td>
        <td data-l="Stand" className="st">
          {has ? <StandPill s={r.stand} /> : <span className="pill p-neu">keine Mandanten</span>}
        </td>
        <td data-l="Mandanten">
          <b className="num">{r.clients.length}</b>
          {has && (
            <span className="sub">
              FiBu {r.fibu} · Lohn {r.lohn}
            </span>
          )}
        </td>
        <td data-l="Belege offen">{r.fibu ? <b className={`num${r.belegeOffen ? ' bad' : ''}`}>{r.belegeOffen}</b> : '–'}</td>
        <td data-l="Zu buchen">{r.fibu ? <Backlog zu={r.zuBuchen} frist={r.nachFrist} /> : '–'}</td>
        <td data-l="Chat">
          <Chat c={r.chat} />
        </td>
      </tr>
      {open && r.clients.map((c, i) => <ClientRow key={c.id} c={c} unassigned={!r.staff} id={i === 0 ? id : undefined} />)}
    </tbody>
  );
}

function ClientRow({ c, unassigned, id }: { c: TeamStatusClient; unassigned: boolean; id?: string }) {
  const navigate = useNavigate();
  const areas = [c.fibu && 'FiBu', c.lohn && 'Lohn'].filter(Boolean).join(' und ') + (unassigned ? ' offen' : '');
  const pill = c.focus ? focusPill(c.focus.state) : null;
  return (
    <tr className="crow click" id={id} onClick={() => navigate(`/kanzlei/mandanten/${c.id}`)}>
      <td>
        {c.country === 'AE' && <span className="flag f-ae">UAE</span>}
        <Link className="rowlink" to={`/kanzlei/mandanten/${c.id}`} onClick={(e) => e.stopPropagation()}>
          {c.name}
        </Link>
        {c.number && <span className="sub">Nr. {c.number}</span>}
      </td>
      <td data-l="Stand" className="st">
        <StandPill s={c.stand} />
      </td>
      <td data-l="Bereich">{areas}</td>
      <td data-l="Belege">
        {c.focus && pill ? (
          <span className={`pill p-${pill.tone === 'none' ? 'neu' : pill.tone}`}>
            {monthShort(c.focus.month)} · {pill.label}
          </span>
        ) : (
          '–'
        )}
      </td>
      <td data-l="Zu buchen">{c.fibu ? <Backlog zu={c.zuBuchen} frist={c.nachFrist} /> : '–'}</td>
      <td data-l="Chat">
        <Chat c={c.chat} />
      </td>
    </tr>
  );
}

function StandPill({ s }: { s: Stand }) {
  return <span className={`pill p-${STAND[s].tone}`}>{STAND[s].label}</span>;
}

function Backlog({ zu, frist }: { zu: number; frist: number }): ReactNode {
  if (!zu) return <span className="num">0</span>;
  return (
    <>
      <b>{monate(zu)}</b>
      {frist > 0 && <span className="sub bad">{frist} nach Frist</span>}
    </>
  );
}

function Chat({ c }: { c: ChatStand | null }): ReactNode {
  if (!c) return '–';
  if (!c.unread) return <span className="num">0</span>;
  return (
    <>
      <b>{c.unread} ungelesen</b>
      {c.since && <span className={`sub${c.late ? ' bad' : ''}`}>seit {fmtDayMonth(c.since)}</span>}
    </>
  );
}
